import Config from "react-global-configuration"
import { KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES } from "../data/enums"
import { getImageSource } from "../components/ImagesViewer/DisplayImage"

const getImageUrlForEditor = (uploadInfo) => {
  const { imageViewerBaseUri, imageViewerBucketName } = Config.get("uploader")
  const imageWidth = 924
  const imageUrl = getImageSource(
    imageViewerBaseUri,
    imageViewerBucketName,
    uploadInfo,
    "cover",
    imageWidth,
    null,
    false
  )
  return imageUrl
}

// Reusable helper functions for media processing
const generateMediaId = (contentItem, mediaType) => {
  // Extract the base type name from the mediaType constant
  const typeMap = {
    [KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.IMAGE]: "image",
    [KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.FILES]: "file",
    [KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.VIDEO]: "video"
  }
  const baseType = typeMap[mediaType] || "media"
  return contentItem.id || `${baseType}-${Date.now()}`
}

const processUploadsForEditor = (uploads, textUploads) => {
  return uploads
    .map((uploadId) => {
      const uploadInfo = textUploads?.find((upload) => upload.id === uploadId)
      return uploadInfo || null
    })
    .filter(Boolean)
}

const processUploadsForSaving = (uploads) => {
  return uploads.map((upload) => (typeof upload === "string" ? upload : upload.id)).filter(Boolean)
}

const processImageUploadsForSaving = (uploads) => {
  const uploadsWithCustomSize = []
  const uploadIds = []

  for (const upload of uploads) {
    const uploadId = typeof upload === "string" ? upload : upload.id
    if (uploadId) {
      uploadIds.push(uploadId)
      uploadsWithCustomSize.push({
        id: uploadId,
        customSize: typeof upload === "object" && upload.customSize?.width ? { width: upload.customSize.width } : null
      })
    }
  }

  return { uploadsWithCustomSize, uploadIds }
}

const isMediaType = (contentItem, mediaType) => {
  return contentItem.type === mediaType
}

// Helper function to transform media nodes (images, files, videos) in lists
const transformMediaNode = (contentItem, textUploads) => {
  // Handle images
  if (isMediaType(contentItem, KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.IMAGE) && Array.isArray(contentItem.uploads)) {
    // Extract upload IDs - contentItem.uploads might contain objects or just IDs
    const uploadIds = contentItem.uploads.map((upload) => (typeof upload === "string" ? upload : upload.id))

    const uploadInfo = textUploads?.find((upload) => uploadIds.includes(upload.id))

    if (uploadInfo && uploadInfo.fileGroup === "image") {
      const imageUrl = getImageUrlForEditor(uploadInfo)
      return {
        ...contentItem,
        attrs: {
          src: imageUrl,
          alt: uploadInfo.fileName || "Image",
          title: uploadInfo.fileName || "Image",
          id: generateMediaId(contentItem, KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.IMAGE),
          uploads: uploadIds.map((uploadId) => {
            const upload = textUploads?.find((u) => u.id === uploadId)
            return {
              id: uploadId,
              customSize: upload?.customSize?.width ? upload.customSize : null
            }
          })
        }
      }
    }
  }

  // Handle files
  if (isMediaType(contentItem, KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.FILES) && Array.isArray(contentItem.uploads)) {
    const fileUploads = processUploadsForEditor(contentItem.uploads, textUploads)

    return {
      ...contentItem,
      attrs: {
        id: generateMediaId(contentItem, KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.FILES),
        uploads: fileUploads
      }
    }
  }

  // Handle videos
  if (isMediaType(contentItem, KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.VIDEO) && contentItem.text) {
    return {
      ...contentItem,
      attrs: {
        id: generateMediaId(contentItem, KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.VIDEO),
        url: contentItem.text
      }
    }
  }

  return null // No transformation needed
}

// Helper function to extract media data from content items for saving
const extractMediaData = (contentItem, uploads) => {
  // Handle images
  if (isMediaType(contentItem, KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.IMAGE) && contentItem.attrs?.uploads) {
    const { uploadsWithCustomSize, uploadIds } = processImageUploadsForSaving(contentItem.attrs.uploads)
    uploads.push(...uploadsWithCustomSize)
    return {
      type: KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.IMAGE,
      uploads: uploadIds
    }
  }

  // Handle files
  if (isMediaType(contentItem, KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.FILES) && contentItem.attrs?.uploads) {
    const fileUploads = processUploadsForSaving(contentItem.attrs.uploads)
    uploads.push(...fileUploads)
    return {
      type: KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.FILES,
      uploads: fileUploads
    }
  }

  // Handle videos
  if (isMediaType(contentItem, KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.VIDEO) && contentItem.attrs?.url) {
    return {
      type: KNOWLEDGE_CONTENT_TIPTAP_TYPENAMES.VIDEO,
      text: contentItem.attrs.url
    }
  }

  return null // No extraction needed
}

export { transformMediaNode, extractMediaData, getImageUrlForEditor }
